/**
 * ============================================================================
 * 🤖 ML-Service Client — Kommunikation mit dem Paketlabel-Erkennungssystem
 * ============================================================================
 * 
 * Endpunkte:
 *   analyzeImage(imageBlob)     — Bild analysieren (Gemini-Regionen + Felder)
 *   orientDocument(blob)        — Dokument aufrichten (PaddleOCR)
 *   reanalyzeRegion(id, box)    — korrigierten Bereich erneut lesen
 *   checkMLHealth()             — Erreichbarkeit prüfen
 *   getMLDetailedStatus()       — KI-Statistiken für den Admin-Bereich
 *
 * Hinweis: Modell-Training, Annotation und Trainingsdaten-Sammlung wurden
 * 07/2026 entfernt — die Erkennung läuft ausschließlich über Gemini.
 */

import { SERVER_URL, TOKEN_KEYS } from '../config/serverConfig';
import { refreshAccessToken } from './apiService';
import { getIgnoreRecipient } from './captureOptions';

const ML_TIMEOUT = 45000; // 45 Sekunden Timeout (muss unter Server-Proxy-Timeout von 60s liegen)

/**
 * Führt einen ML-Request aus und erneuert bei 401 einmalig den Access-Token
 * (analog zu apiFetch). Ohne das schlägt die KI-Analyse am Tablet fehl, sobald
 * der 15-Minuten-Access-Token abgelaufen ist → „KI-Analyse fehlgeschlagen".
 * @param {(token: string) => Promise<Response>} doFetch - baut & sendet den Request mit gegebenem Token
 * @returns {Promise<Response>}
 */
async function mlFetchWithRefresh(doFetch) {
  let response = await doFetch(getToken());
  if (response.status === 401) {
    try {
      const fresh = await refreshAccessToken(); // erneuert Token + speichert in localStorage
      response = await doFetch(fresh || getToken());
    } catch {
      /* Refresh gescheitert (Session endgültig weg) → normalen Fehlerpfad laufen lassen */
    }
  }
  return response;
}

/**
 * Analysiert ein Paketlabel-Foto mit dem ML-Service.
 * 
 * @param {Blob|File} imageBlob - Das Foto als Blob
 * @returns {Promise<{
 *   imageId: string,
 *   regions: Array<{type: string, box: {x,y,w,h}, confidence: number}>,
 *   thumbnail: string|null,     // Base64-JPEG des erkannten Bereichs
 *   ocrResult: {name, street, postal, city, rawText, blocks}|null,
 *   modelVersion: string,
 *   processingTime: number
 * }>}
 */
export async function analyzeImage(imageBlob, options = {}) {
  // FormData pro Versuch neu bauen (nach Token-Refresh wird erneut gesendet).
  const buildBody = () => {
    const formData = new FormData();
    formData.append('image', imageBlob, 'paket.jpg');
    // Optionaler Center-Crop für Gemini (nur Bench-Decken-Kamera): Vollbild wird
    // gespeichert, Gemini sieht nur die mittige Label-Zone (billiger + fokussierter).
    if (options.cropPercent && options.cropPercent > 0) {
      formData.append('crop_percent', String(options.cropPercent));
    }
    const ignoreRec = (options.ignoreRecipient !== undefined ? String(options.ignoreRecipient || '') : getIgnoreRecipient()).trim();
    if (ignoreRec) {
      formData.append('ignore_recipient', ignoreRec.slice(0, 200));
    }
    return formData;
  };

  // ⏳ Server/Erkennung kurz weg (Deploy, Neustart, WLAN-Aussetzer): warten und erneut versuchen statt sofort auf die
  // schwache Texterkennung im Browser zurückzufallen (23.10.2026). Nur bei Netzfehler/502/503 — eine Zeitüberschreitung
  // (Gemini langsam) wird NICHT wiederholt. options.onWarte({ versuch, seitMs, maxMs }) für einen Ladebalken.
  const start = Date.now();
  const maxMs = options.maxWarteMs ?? 60000;
  let versuch = 0;
  let response;
  for (;;) {
    let netzweg = false;
    try {
      response = await mlFetchWithRefresh((token) => fetch(
        `${getMLBaseUrl()}/analyze`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: buildBody(),
          signal: AbortSignal.timeout(ML_TIMEOUT),
        }
      ));
    } catch (e) {
      if (e && (e.name === 'TimeoutError' || e.name === 'AbortError')) throw e;
      if (Date.now() - start >= maxMs) throw e;
      netzweg = true;
    }
    const kurzWeg = netzweg || response.status === 502 || response.status === 503;
    if (!kurzWeg || Date.now() - start >= maxMs) break;
    versuch += 1;
    try { if (options.onWarte) options.onWarte({ versuch, seitMs: Date.now() - start, maxMs }); } catch { /* */ }
    await new Promise((res) => setTimeout(res, Math.min(1000 * versuch, 4000)));
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // Wenn ML-Service nicht verfügbar → Fallback signalisieren
    if (response.status === 503 || response.status === 504 || error.fallback) {
      return { fallback: true, error: error.message || 'ML-Service nicht verfügbar' };
    }
    throw new Error(error.message || `ML-Analyse fehlgeschlagen (${response.status})`);
  }
  
  return response.json();
}

/**
 * Liest ausschließlich Barcodes (per Server zxing-cpp inkl. Upscaling) aus einem Bild/Frame,
 * ohne Gemini aufzurufen (< 200 ms, 0 Tokens). Ideal für „Etikett um die Ecke"-Nachscans.
 * @param {Blob|File} imageBlob
 * @returns {Promise<Array<{text: string, format: string}>>}
 */
export async function readBarcodesOnly(imageBlob) {
  try {
    const response = await mlFetchWithRefresh((token) => {
      const fd = new FormData();
      fd.append('image', imageBlob, 'barcode.jpg');
      return fetch(`${getMLBaseUrl()}/barcodes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
        signal: AbortSignal.timeout(8000),
      });
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data?.barcodes) ? data.barcodes : [];
  } catch {
    return [];
  }
}

/**
 * 🧭 Richtet ein Dokument-Foto auf (Ausweis/Label/Abholkarte).
 *
 * Der ML-Service bestimmt den Drehwinkel per PaddleOCR (Schräglage aus den Textzeilen +
 * Quadrant aus der Zeilen-Geometrie) und liefert das fertig gedrehte Bild zurück.
 * Gemini kann das NICHT — gemessen, siehe ml-service/models/ocr.py::orientation().
 *
 * Findet der Dienst zu wenig Text, kommt das Bild unverändert zurück (rotated=false) —
 * lieber ungedreht als falsch gedreht.
 *
 * @param {Blob} blob      Bild (idealerweise schon auf das Dokument zugeschnitten)
 * @param {string} nameHint Erkannter Name — verankert die „oben"-Richtung
 * @returns {Promise<{angle:number, rotated:boolean, image:string}|null>} null bei Fehler
 */
export async function orientDocument(blob, nameHint = '') {
  try {
    const response = await mlFetchWithRefresh((token) => {
      const fd = new FormData();
      fd.append('image', blob, 'doc.jpg');
      if (nameHint) fd.append('name_hint', nameHint);
      return fetch(`${getMLBaseUrl()}/orient`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
        signal: AbortSignal.timeout(ML_TIMEOUT),
      });
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null; // Aufrichten ist Komfort, kein Muss → Ablauf nie blockieren
  }
}

/**
 * Re-analysiert einen manuell korrigierten Bildbereich.
 * Der User hat den Empfänger-Bereich auf dem Originalbild verschoben/vergrößert.
 * 
 * @param {string} imageId - ID des ursprünglich analysierten Bildes
 * @param {Object} box - Korrigierter Bereich in Original-Pixel-Koordinaten
 * @param {number} box.x
 * @param {number} box.y
 * @param {number} box.w
 * @param {number} box.h
 * @returns {Promise<{imageId, thumbnail, ocrResult, correctedBox, processingTime, regions}>}
 */
export async function reanalyzeRegion(imageId, box) {
  const buildBody = () => {
    const formData = new FormData();
    formData.append('image_id', imageId);
    formData.append('box_x', box.x.toString());
    formData.append('box_y', box.y.toString());
    formData.append('box_w', box.w.toString());
    formData.append('box_h', box.h.toString());
    return formData;
  };

  const response = await mlFetchWithRefresh((token) => fetch(
    `${getMLBaseUrl()}/reanalyze`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: buildBody(),
      signal: AbortSignal.timeout(ML_TIMEOUT),
    }
  ));

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || error.message || `Re-Analyse fehlgeschlagen (${response.status})`);
  }

  return response.json();
}

/**
 * Prüft ob der ML-Service erreichbar ist.
 */
/**
 * 📦 Vinted-Go-Shops-Screenshot → Paare Kurznummer ↔ Name (Sonderfunktion „vintedImport").
 * Server prüft die Freischaltung pro Konto (403 sonst).
 * @returns {Promise<Array<{code:string, name:string}>>}
 * @throws {Error} mit .status (403 = nicht freigeschaltet)
 */
// ── 📷 Fotos ohne Erkennung (Schadensdokumentation) ──────────────────────────
// Gleiche Ablage wie das Erfassungsfoto, nur ohne KI; der Dienst prüft den Eigentümer.

/** Foto verkleinern (längste Seite 1600 px) — schneller Upload, reicht für Reklamationen */
async function verkleinern(datei, maxKante = 1600) {
  try {
    const bmp = await createImageBitmap(datei);
    const f = Math.min(1, maxKante / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * f); c.height = Math.round(bmp.height * f);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    if (bmp.close) bmp.close();
    // synchron (toDataURL): toBlob wird im WebView gedrosselt
    const bin = atob(c.toDataURL('image/jpeg', 0.85).split(',')[1]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: 'image/jpeg' });
  } catch {
    return datei; // nicht dekodierbar → Original schicken, der Dienst verkleinert selbst
  }
}

/** Foto speichern → Bild-ID */
export async function uploadFoto(datei) {
  const blob = await verkleinern(datei);
  const response = await mlFetchWithRefresh((token) => {
    const fd = new FormData();
    fd.append('image', blob, 'foto.jpg');
    return fetch(`${getMLBaseUrl()}/image`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd, signal: AbortSignal.timeout(ML_TIMEOUT),
    });
  });
  if (!response.ok) throw new Error(`Foto: HTTP ${response.status}`);
  const data = await response.json();
  if (!data || !data.imageId) throw new Error('Foto: keine Bild-ID');
  return data.imageId;
}

const _fotoUrls = new Map();
/** Foto als Blob-URL laden (zwischengespeichert) — size: 'thumb' | 'full' */
export async function ladeFotoUrl(imageId, size = 'thumb') {
  const key = `${imageId}|${size}`;
  if (_fotoUrls.has(key)) return _fotoUrls.get(key);
  const response = await mlFetchWithRefresh((token) => fetch(`${getMLBaseUrl()}/image/${encodeURIComponent(imageId)}?size=${size}`, {
    headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(ML_TIMEOUT),
  }));
  if (!response.ok) throw new Error(`Foto: HTTP ${response.status}`);
  const url = URL.createObjectURL(await response.blob());
  _fotoUrls.set(key, url);
  return url;
}

/** Foto löschen (z. B. hochgeladen, aber das Formular wurde ohne Speichern geschlossen) */
export async function loescheFoto(imageId) {
  try {
    await mlFetchWithRefresh((token) => fetch(`${getMLBaseUrl()}/image/${encodeURIComponent(imageId)}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    }));
  } catch { /* nicht kritisch */ }
}

export async function extractVintedList(blob) {
  const response = await mlFetchWithRefresh((token) => {
    const fd = new FormData();
    fd.append('image', blob, 'vinted.jpg');
    return fetch(`${getMLBaseUrl()}/vinted-list`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
      signal: AbortSignal.timeout(ML_TIMEOUT),
    });
  });
  if (!response.ok) {
    const err = new Error(`Vinted-Liste: HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  const data = await response.json();
  return Array.isArray(data?.entries) ? data.entries : [];
}

/**
 * 📋 Einlieferungsbeleg-Scanner für den Paketzähler (für alle Nutzer):
 * Liest aus einem Foto des Einlieferungsbelegs alle Sendungsnummern + Gesamtzahl aus.
 */
export async function extractDeliveryReceipt(blob) {
  const response = await mlFetchWithRefresh((token) => {
    const fd = new FormData();
    const fileName = blob?.name || (blob?.type === 'application/pdf' ? 'receipt.pdf' : 'receipt.jpg');
    fd.append('image', blob, fileName);
    return fetch(`${getMLBaseUrl()}/delivery-receipt`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
      signal: AbortSignal.timeout(ML_TIMEOUT),
    });
  });
  if (!response.ok) {
    const err = new Error(`Einlieferungsbeleg: HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  const data = await response.json();
  const rawCodes = Array.isArray(data?.codes)
    ? data.codes
    : (Array.isArray(data?.trackingNumbers) ? data.trackingNumbers : []);
  return {
    codes: rawCodes,
    totalCount: Number(data?.totalCount) || rawCodes.length,
    slips: Array.isArray(data?.slips) ? data.slips : [],
    carrier: data?.carrier || null,
    notes: data?.notes || null,
  };
}

export async function checkMLHealth() {
  try {
    const response = await fetch(`${getMLBaseUrl()}/health`, {
      signal: AbortSignal.timeout(5000),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Ruft umfangreiche KI-Detailstatistiken ab (Admin-only).
 * Aggregiert: ML-Health, Model-Status, OCR-Pipeline, Server-Stats, Tagesstatistiken.
 */
export async function getMLDetailedStatus() {
  const response = await fetch(`${SERVER_URL}/admin/ml-detailed`, {
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(15000),
  });

  if (!response.ok) {
    console.warn('⚠️ ML-Detailstatus nicht verfügbar:', response.status);
    return null;
  }

  return response.json();
}

// ============================================================================
// Helpers
// ============================================================================

function getMLBaseUrl() {
  // ML-Proxy läuft über den gleichen Server unter /ml
  return `${SERVER_URL}/ml`;
}

function getToken() {
  // Token aus localStorage (vermeidet CJS require + zirkuläre Abhängigkeit)
  try {
    return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) || '';
  } catch {
    return '';
  }
}
